import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { Layers, Filter, ChevronRight, User, Calendar } from "lucide-react";

const STAGE_LABELS = {
  requirement_collection: "Requirements",
  design: "Designing",
  internal_review: "Internal QC",
  client_review: "Client Review",
  revisions: "Revisions",
  approval: "Approval",
  delivery: "Delivery",
  completed: "Completed",
};
const STAGE_COLOR = {
  requirement_collection: "bg-amber-50 text-amber-700 border-amber-200",
  design: "bg-blue-50 text-blue-700 border-blue-200",
  internal_review: "bg-purple-50 text-purple-700 border-purple-200",
  client_review: "bg-orange-50 text-orange-700 border-orange-200",
  revisions: "bg-rose-50 text-rose-700 border-rose-200",
  approval: "bg-cyan-50 text-cyan-700 border-cyan-200",
  delivery: "bg-teal-50 text-teal-700 border-teal-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Projects() {
  const [data, setData] = useState({ stages: [], columns: {}, labels: {} });
  const [view, setView] = useState("board");

  useEffect(() => {
    api.get("/projects/board").then(r => setData(r.data));
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-projects">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-[#1a3c5e]">Projects (Workflow Board)</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setView("board")} data-testid="projects-view-board" className={`px-3 py-1.5 rounded-xl text-xs font-bold ${view === "board" ? "bg-[#1a3c5e] text-white" : "bg-white border border-gray-300"}`}>Board</button>
          <button onClick={() => setView("list")} data-testid="projects-view-list" className={`px-3 py-1.5 rounded-xl text-xs font-bold ${view === "list" ? "bg-[#1a3c5e] text-white" : "bg-white border border-gray-300"}`}>List</button>
        </div>
      </div>

      {view === "board" && (
        <div className="overflow-x-auto pb-4">
          <div className="inline-flex gap-3 min-w-full">
            {(data.stages || []).map(s => (
              <div key={s} className="w-72 flex-shrink-0 bg-gray-50 border border-gray-200 rounded-2xl p-2" data-testid={`proj-col-${s}`}>
                <div className={`px-3 py-2 rounded-xl border ${STAGE_COLOR[s]} text-xs font-bold uppercase flex justify-between`}>
                  <span>{STAGE_LABELS[s] || s}</span>
                  <span>{(data.columns?.[s] || []).length}</span>
                </div>
                <div className="mt-2 space-y-2">
                  {(data.columns?.[s] || []).map(p => (
                    <Link key={p.id} to={`/admin/projects/${p.id}`} data-testid={`proj-card-${p.id}`}
                      className="block bg-white rounded-xl border border-gray-200 p-3 hover:shadow-md transition">
                      <div className="text-sm font-bold text-[#1a3c5e]">{p.package_title || "House Design"}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{p.customer?.name} · {p.customer?.city || "—"}</div>
                      <div className="flex items-center justify-between mt-2 text-[10px]">
                        <span className="text-gray-500">#{p.id.slice(0, 8).toUpperCase()}</span>
                        <span className="font-bold text-[#F7941D]">{inr(p.amount || 0)}</span>
                      </div>
                      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-1.5 bg-[#F7941D]" style={{ width: `${p.workflow_progress || 0}%` }} />
                      </div>
                      <div className="mt-1.5 flex gap-2 text-[10px] text-gray-500">
                        {p.files_count > 0 && <span>📂 {p.files_count}</span>}
                        {p.tasks_count > 0 && <span>✅ {p.tasks_count}</span>}
                        {p.approvals_pending > 0 && <span className="text-rose-500">⚠ {p.approvals_pending}</span>}
                      </div>
                    </Link>
                  ))}
                  {(data.columns?.[s] || []).length === 0 && <div className="text-center py-6 text-gray-400 text-xs">Empty</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "list" && <ListView columns={data.columns} />}
    </div>
  );
}

function ListView({ columns }) {
  const all = Object.values(columns || {}).flat();
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#FEF3E2] text-[#1a3c5e] text-xs uppercase">
          <tr>
            <th className="text-left px-4 py-3">Project</th>
            <th className="text-left px-4 py-3">Customer</th>
            <th className="text-left px-4 py-3">Stage</th>
            <th className="text-left px-4 py-3">Progress</th>
            <th className="text-right px-4 py-3">Value</th>
          </tr>
        </thead>
        <tbody>
          {all.map(p => (
            <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3"><Link to={`/admin/projects/${p.id}`} className="font-bold text-[#1a3c5e]" data-testid={`proj-list-${p.id}`}>{p.package_title}</Link><div className="text-xs text-gray-500">#{p.id.slice(0, 8).toUpperCase()}</div></td>
              <td className="px-4 py-3">{p.customer?.name}</td>
              <td className="px-4 py-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${STAGE_COLOR[p.workflow_stage]}`}>{STAGE_LABELS[p.workflow_stage] || p.workflow_stage}</span></td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2"><div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-1.5 bg-[#F7941D]" style={{ width: `${p.workflow_progress}%` }} /></div><span className="text-xs">{p.workflow_progress}%</span></div>
              </td>
              <td className="px-4 py-3 text-right font-bold">{inr(p.amount || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
